/**
 * A component for collecting, uploading, and managing multiple document collections.
 * Supports multiple profiles with their own document types in a single form.
 */
import React, { useState, useMemo, useCallback } from "react";
import { Button, Flex, Text } from "@jsq/ds/core";
import { useFormContext, Form } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { DocumentFormValues } from "../../../hooks/documents/useDocumentProcessor";
import { DefaultModal } from "../../common/DefaultModal";
import { SingleFileUpload } from "../../common/SingleFileUpload";
import { DocumentData } from "../DocumentCollector/types";
import { prepareFilesToUpload } from "../DocumentCollector/utils";
import { collectionContainer } from "./style_vanilla.css";
import { MultiDocumentCollectorProps, DocumentCollection } from "./types";

// Supported file formats that can be uploaded through the document collector
const SUPPORTED_FORMATS = ["image/*", ".pdf", ".doc", ".docx", ".xlsx", ".xls", ".csv", ".zip"];

/**
 * Renders the form fields for a single document collection
 */
const DocumentCollectionForm: React.FC<{
  collection: DocumentCollection;
  fieldIndexOffset: number;
}> = ({ collection, fieldIndexOffset }) => {
  const formContext = useFormContext<{ documents: DocumentData[] }>();

  return (
    <Flex flexDirection="column" gap={Sizes.MD}>
      {collection.title && (
        <Flex flexDirection="column" gap={Sizes.XS}>
          <Text variant="h4">{collection.title}</Text>
        </Flex>
      )}
      {collection.documentDetails.map((document, index) => {
        const fieldIndex = fieldIndexOffset + index;

        return (
          <Flex key={index} flexDirection="column">
            <SingleFileUpload
              title={document.title}
              description={document.description}
              initialFile={document.file}
              acceptedFileFormats={SUPPORTED_FORMATS}
              onFileSelected={(file) => {
                void formContext.setFieldValue(`documents[${fieldIndex}].file`, file);
                return;
              }}
              onFileDeleted={() => {
                void formContext.setFieldValue(`documents[${fieldIndex}].file`, null);
                return;
              }}
            />
          </Flex>
        );
      })}
    </Flex>
  );
};

/**
 * Main component for collecting multiple document collections with support for uploading, replacing,
 * and deleting documents across multiple profiles.
 */
export const MultiDocumentCollector: React.FC<MultiDocumentCollectorProps> = ({
  documentCollections,
  onNavigateBack,
  onComplete,
  isSubmitting,
  missingDocumentsMessage,
}) => {
  const [documentFormValues, setDocumentFormValues] = useState<
    { documents: DocumentData[] } | undefined
  >();

  // Flatten all documents from all collections into a single array for the form
  const initialDocuments = useMemo<Array<DocumentData>>(() => {
    return documentCollections.flatMap((collection, collectionIndex) =>
      collection.documentDetails.map((detail, detailIndex) => ({
        id: detail.file?.uuid ? Number(detail.file.uuid) : undefined,
        file: detail.file,
        baseType: detail.baseType,
        subType: detail.subType,
        // Store metadata for processing
        meta: {
          profileId: collection.profileId,
          collectionIndex,
          detailIndex,
        },
      })),
    );
  }, [documentCollections]);

  // Calculate field index offsets for each collection
  const collectionOffsets = useMemo(() => {
    const offsets: number[] = [];
    let currentOffset = 0;

    documentCollections.forEach((collection) => {
      offsets.push(currentOffset);
      currentOffset += collection.documentDetails.length;
    });

    return offsets;
  }, [documentCollections]);

  const uploadHandler = useCallback(
    (newSelectedFiles: DocumentFormValues) => {
      const { replacedFiles, newUploadFiles } = prepareFilesToUpload(
        newSelectedFiles,
        initialDocuments,
      );
      onComplete(replacedFiles, newUploadFiles);
    },
    [initialDocuments, onComplete],
  );

  /**
   * Process documents for all collections
   */
  const handleSubmit = useCallback(
    (formValues: { documents: DocumentData[] }) => {
      const documentsNotAvailable = formValues.documents.some(
        (document: DocumentData) => document.file == null,
      );
      if (documentsNotAvailable) {
        setDocumentFormValues(formValues);
      } else {
        uploadHandler(formValues);
      }
    },
    [uploadHandler],
  );

  return (
    <div>
      <Form<{ documents: DocumentData[] }>
        initialValues={{
          documents: initialDocuments,
        }}
        submitOnlyChangedValues={false}
        onSubmit={handleSubmit}
      >
        <Flex flexDirection="column" gap={Sizes.LG}>
          {documentCollections.map((collection, index) => (
            <div key={index} className={collectionContainer}>
              <DocumentCollectionForm
                collection={collection}
                fieldIndexOffset={collectionOffsets[index]}
              />
            </div>
          ))}

          <Flex flexDirection="row" gap="10px" justifyContent="flex-end">
            {onNavigateBack && (
              <Button variant="neutral" onClick={onNavigateBack} disabled={isSubmitting}>
                Back
              </Button>
            )}
            <Button type="submit" isLoading={isSubmitting}>
              Complete
            </Button>
          </Flex>
        </Flex>
      </Form>

      <DefaultModal
        show={!!documentFormValues}
        modalSize="medium"
        title="Missing document warning"
        body={missingDocumentsMessage}
        negBtnText="Cancel"
        posBtnText="Continue"
        onClose={() => setDocumentFormValues(undefined)}
        onConfirm={() => {
          uploadHandler(documentFormValues!);
          setDocumentFormValues(undefined);
        }}
      />
    </div>
  );
};
